import React, { useState, useRef, useEffect, forwardRef } from 'react';
import cn from 'classnames';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import '../../../styles/modules/search.less';
import '../../../styles/modules/waiver-reporting.less';
import TimePicker from '../TimePicker';
import moment from 'moment';
import YearSelect from './YearSelect';
import CustomInput from './CustomInput';
import MonthSelect from './MonthSelect';
import { validateDate } from '../filters';
import './dateSelector.less';

interface DateSelectorProps {
  label?: string;
  value: string | null;
  time?: Date;
  dateFormat?: string;
  required?: boolean;
  includeTime?: boolean;
  showMonthYearPicker?: boolean;
  onChange?: (date: Date | null) => void;
  className?: string;
  placeholderText?: string;
  initialTime?: string;
  maxDate?: string;
  onClick?: () => void;
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
}

const DateSelector: React.FC<DateSelectorProps> = ({
  label,
  value,
  time,
  dateFormat = 'MMMM yyyy',
  required = false,
  includeTime,
  showMonthYearPicker = false,
  onChange,
  className = '',
  placeholderText,
  initialTime,
  maxDate,
  onClick = () => {},
  onBlur = () => {}
}) => {
  const [date, setDate] = useState<Date | null>(value && value !== '' ? new Date(value) : null);
  const [timeValue, setTimeValue] = useState<Date | null>(time || null);
  const datePickerRef = useRef<DatePicker>(null);
  const [inputClassName, setInputClassName] = useState('');

  const inputId = Math.random().toString(16).slice(2);

  useEffect(() => {
    if (value === null || value === '') {
      setDate(null);
      setTimeValue(null);
    } else {
      setDate(new Date(value));
    }
  }, [value]);

  const getLabel = () => `${label}${required ? '*' : ''}`;

  const handleDateChange = (date: Date | null) => {
    setInputClassName('');
    setDate(date);
    updateDateTime(date, timeValue);
  };

  const handleTimeChange = (time: string) => {
    const [timePart, modifier] = time.split(' ');
    let [hours, minutes] = timePart.split(':').map(Number);

    if (modifier === 'PM' && hours < 12) {
      hours += 12;
    } else if (modifier === 'AM' && hours === 12) {
      hours = 0;
    }

    const newTime = new Date();
    newTime.setHours(hours);
    newTime.setMinutes(minutes);
    setTimeValue(newTime);
    updateDateTime(date, newTime);
  };

  const updateDateTime = (newDate: Date | null, newTime: Date | null) => {
    if (newDate && includeTime && newTime) {
      newDate.setHours(newTime.getHours());
      newDate.setMinutes(newTime.getMinutes());
    }
    onChange && onChange(newDate);
  };

  const handleRawChange = (event?: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>) => {
    if (!event) return;
    const target = event.target as HTMLInputElement;
    const value = target.value;
    if (value) {
      const isInvalidDate = validateDate(value);
      if (isInvalidDate) {
        setInputClassName('ng-invalid ng-dirty');
      } else {
        setInputClassName('ng-valid ng-dirty');
        onChange && onChange(moment(value, dateFormat).toDate());
      }
    }
  }

  const handleButtonClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.stopPropagation();
    datePickerRef.current?.setOpen(true);
    onClick();
  };

  const handleTodayClick = () => {
    const today = new Date();
    setDate(today);
    updateDateTime(today, timeValue);
    datePickerRef.current?.setOpen(false);
  };

  const handleClearClick = () => {
    setDate(null);
    setTimeValue(null);
    onChange && onChange(null);
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if(e.key === 'Escape') {
      datePickerRef.current?.setOpen(false);
    }
  }
  const RenderCustomFooter = () => (
    <div className="custom-footer center-align">
      <button type="button" className="btn btn-clear" onClick={handleClearClick}>
        Clear
      </button>
      <button type="button" className="btn btn-today" onClick={handleTodayClick}>
        Today
      </button>
    </div>
  );

  return (
    <>
      <div
        className={cn('date-picker-wrapper', {
          'col-xs-6 col-sm-6 col-md-6': includeTime,
          'col-xs-12 col-sm-12 col-md-12': !includeTime,
        })}
      >
        {label && (
          <label htmlFor={inputId} className="date-picker-label">
            {getLabel()}
          </label>
        )}
        <div className="input-group date-picker-input date-selector-input">
          <DatePicker
            ref={datePickerRef}
            id={inputId}
            selected={date}
            onChange={handleDateChange}
            onChangeRaw={handleRawChange}
            dateFormat={dateFormat}
            showMonthYearPicker={showMonthYearPicker}
            showFullMonthYearPicker={showMonthYearPicker}
            showMonthDropdown
            showYearDropdown
            yearDropdownItemNumber={5}
            scrollableYearDropdown
            onInputClick={() => onClick()}
            onBlur={onBlur}
            dropdownMode="select"
            maxDate={maxDate ? new Date(maxDate) : undefined}
            className={cn(`form-control ${inputClassName}`, { 'date-with-time': includeTime })}
            placeholderText={placeholderText}
            required={required}
            popperClassName={cn('uib-datepicker-popup', {
              'full-width': showMonthYearPicker,
            })}
            fixedHeight
            autoFocus
            renderCustomHeader={({
              date,
              changeYear,
              changeMonth,
            }) => (
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                }}
              >
                {!showMonthYearPicker && <MonthSelect
                  date={date}
                  changeMonth={changeMonth}
                  onKeyDown={onKeyDown}
                />}
                <YearSelect
                  date={date}
                  changeYear={changeYear}
                  onKeyDown={onKeyDown}
                />
              </div>
            )}
            customInput={<CustomInput className={className}/>}
          >
            <RenderCustomFooter />
          </DatePicker>
          <span className="input-group-btn">
            <button
              type="button"
              className={`btn btn-default  ${className}`}
              onClick={handleButtonClick}
            >
              <i className="sprites sprite-calendar-gray"></i>
            </button>
          </span>
        </div>
      </div>

      {includeTime && (
        <div className="timepicker col-xs-6 col-sm-6 col-md-6">
          <TimePicker onChange={handleTimeChange} initialValue={initialTime} />
        </div>
      )}
    </>
  );
};

export default DateSelector;